/**
 * HR Page Manager
 * Handles showing/hiding sections based on URL parameters
 * Compatible with Shopify theme editor
 */

class HRPageManager {
    constructor() {
        this.initialized = false;
        this.rules = [
            {
                param: "hr-page",
                value: "true",
                hideBehaviors: ["hide"],
                showBehaviors: ["show"],
            },
        ];

        this.init();
    }

    init() {
        if (this.initialized) return;
        this.initialized = true;

        // Use theme's DOM ready function if available, otherwise fallback
        if (window.theme && theme.DOMready) {
            theme.DOMready(() => this.processRules());
        } else {
            if (document.readyState === "loading") {
                document.addEventListener("DOMContentLoaded", () =>
                    this.processRules()
                );
            } else {
                this.processRules();
            }
        }

        // Handle theme editor events
        if (window.Shopify && Shopify.designMode) {
            document.addEventListener("shopify:section:load", () =>
                this.processRules()
            );
            document.addEventListener("shopify:section:reorder", () =>
                this.processRules()
            );
        }
    }

    processRules() {
        const params = new URLSearchParams(window.location.search);

        // Always show sections with default behavior
        this.showSectionsByBehavior(["default"]);

        this.rules.forEach((rule) => {
            const paramValue = params.get(rule.param);

            if (paramValue === rule.value) {
                // HR mode is active
                this.hideSectionsByBehavior(rule.hideBehaviors);
                this.showSectionsByBehavior(rule.showBehaviors);
            } else {
                // HR mode is not active
                this.showSectionsByBehavior(rule.hideBehaviors);
                this.hideSectionsByBehavior(rule.showBehaviors);
            }
        });
    }

    hideSectionsByBehavior(behaviors) {
        behaviors.forEach((behavior) => {
            const sections = document.querySelectorAll(
                `[data-hr-behavior="${behavior}"]`
            );
            sections.forEach((section) => {
                const parentElement = section.parentElement;
                if (parentElement && parentElement.style.display !== "none") {
                    parentElement.style.display = "none";
                    parentElement.setAttribute("data-hr-hidden", "true");
                }
            });
        });
    }

    showSectionsByBehavior(behaviors) {
        behaviors.forEach((behavior) => {
            const sections = document.querySelectorAll(
                `[data-hr-behavior="${behavior}"]`
            );
            sections.forEach((section) => {
                const parentElement = section.parentElement;
                if (
                    parentElement &&
                    parentElement.hasAttribute("data-hr-hidden")
                ) {
                    parentElement.style.display = "";
                    parentElement.removeAttribute("data-hr-hidden");
                }
            });
        });
    }

    // Method to add new rules dynamically
    addRule(rule) {
        this.rules.push(rule);
        this.processRules();
    }

    // Method to remove rules
    removeRule(param) {
        this.rules = this.rules.filter((rule) => rule.param !== param);
        this.processRules();
    }

    // Method to get current HR page state
    isHRPage() {
        const params = new URLSearchParams(window.location.search);
        return params.get("hr-page") === "true";
    }

    // Method to toggle HR page mode
    toggleHRPage() {
        const params = new URLSearchParams(window.location.search);
        const currentState = params.get("hr-page") === "true";
        params.set("hr-page", (!currentState).toString());

        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.pushState({}, "", newUrl);

        this.processRules();
    }
}

// Initialize the manager when the script loads
if (typeof window !== "undefined") {
    window.HRPageManager = new HRPageManager();
}

// Export for module systems
if (typeof module !== "undefined" && module.exports) {
    module.exports = HRPageManager;
}
